import { OrderStatus, RECEIPT_STATUS } from "../../../models/receipt/types";
import { TFunction } from "i18next";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const mapReceiptStatusToOrderStatus = (status: string): OrderStatus => {
  switch (status) {
    case RECEIPT_STATUS.PENDING:
    case RECEIPT_STATUS.PENDING_PAYMENT:
    case RECEIPT_STATUS.ACCEPTED:
    case RECEIPT_STATUS.CHECK_IN:
      return "scheduled";
    case RECEIPT_STATUS.APPROACHING:
      return "enroute";
    case RECEIPT_STATUS.ARRIVED:
    case RECEIPT_STATUS.ON_GOING:
      return "intransit";
    case RECEIPT_STATUS.SUCCESSFUL:
    case RECEIPT_STATUS.COMPLETED:
      return "completed";
    case RECEIPT_STATUS.CANCELLED:
    case RECEIPT_STATUS.TIMED_OUT:
    case RECEIPT_STATUS.EXPIRED:
    case RECEIPT_STATUS.FAILED:
      return "cancelled";
    default:
      return "scheduled";
  }
};

export const getMainTitle = (
  t: TFunction,
  status: OrderStatus,
  timeString?: string,
  pointOfInterest?: string,
  etaSeconds?: number | null,
  rawApiStatus?: string,
  pickupDisplayName?: string
): string => {
  const tripTime = timeString ? dayjs(timeString) : null;
  const now = dayjs();

  switch (status) {
    case "scheduled":
      if (!tripTime) return "";

      if (tripTime.isSame(now, "day")) {
        return t("commonToday") + ", " + tripTime.format("h:mm A");
      } else if (tripTime.isSame(now.clone().subtract(1, "day"), "day")) {
        return t("commonYesterday") + ", " + tripTime.format("h:mm A");
      } else if (tripTime.isSame(now.clone().add(1, "day"), "day")) {
        return t("commonTomorrow") + ", " + tripTime.format("h:mm A");
      } else if (tripTime.isAfter(now, "day")) {
        const weekdayKey = "common" + tripTime.format("dddd");
        return t(weekdayKey) + ", " + tripTime.format("h:mm A");
      } else {
        const weekdayKey = "common" + tripTime.format("dddd");
        const weekday = t(weekdayKey);
        const year = tripTime.format("YYYY");
        const month = tripTime.format("MM");
        const day = tripTime.format("DD");
        const h = tripTime.format("h");
        const mm = tripTime.format("mm");
        const meridiem = tripTime.format("A");
        return t("commonDateTime", { weekday, year, month, day, h, mm, meridiem });
      }
    case "enroute":
      // data.live.eta is the only source of driver-ETA minutes; null shows no title.
      if (typeof etaSeconds !== "number") return "";
      return t("driverEnrouteSubtitle", {
        n: Math.max(1, Math.round(etaSeconds / 60)),
      });
    case "intransit":
      if (rawApiStatus === "ARRIVED") {
        return pickupDisplayName || '';
      }
      return pointOfInterest || '';
    case "completed":
      return t("tripCompletedTitle");
    case "cancelled":
      return t("tripCancelledTitle");
    default:
      return "";
  }
};

export const getSubtitle = (
  t: TFunction,
  status: OrderStatus,
  timeString?: string,
  rawApiStatus?: string
): string => {
  const tripTime = timeString ? dayjs(timeString) : null;
  const now = dayjs();

  switch (status) {
    case "scheduled":
      return t("tripScheduledTitle");
    case "enroute":
      // if (diffMin > 15) {
      //   return t("tripScheduledTitle");
      // }
      return t("driverEnrouteTitle");
    case "intransit":
      if (rawApiStatus === "ARRIVED") {
        return t("driverArrived_title");
      }
      return t("headingToDestinationTitle");
    case "completed":
    case "cancelled":
      if (!tripTime) return "";
      if (tripTime.isSame(now, "day")) {
        return t("commonToday") + ", " + tripTime.format("h:mm A");
      } else if (tripTime.isSame(now.clone().subtract(1, "day"), "day")) {
        return t("commonYesterday") + ", " + tripTime.format("h:mm A");
      } else if (tripTime.isSame(now.clone().add(1, "day"), "day")) {
        return t("commonTomorrow") + ", " + tripTime.format("h:mm A");
      } else if (tripTime.isAfter(now, "day")) {
        const weekdayKey = "common" + tripTime.format("dddd");
        return t(weekdayKey) + ", " + tripTime.format("h:mm A");
      } else {
        const weekdayKey = "common" + tripTime.format("dddd");
        const weekday = t(weekdayKey);
        const year = tripTime.format("YYYY");
        const month = tripTime.format("MM");
        const day = tripTime.format("DD");
        const h = tripTime.format("h");
        const mm = tripTime.format("mm");
        const meridiem = tripTime.format("A");
        return t("commonDateTime", {
          weekday,
          year,
          month,
          day,
          h,
          mm,
          meridiem,
        });
      }
    default:
      return "";
  }
};

export const getTimeInfo = (
  t: TFunction,
  status: OrderStatus,
  timeString?: string,
  etaSeconds?: number | null,
  rawApiStatus?: string
): string => {
  if (status === "intransit") {
    if (rawApiStatus === "ON_GOING" && typeof etaSeconds === "number") {
      const eta = dayjs().add(etaSeconds, "second");
      const h_mm = eta.format("h:mm");
      const meridiem = eta.format("A");
      return t("headingToDestinationEta", { "h:mm": h_mm, meridiem });
    }
    return "";
  }
  if (!timeString) return "";
  const tripTime = dayjs(timeString);
  const now = dayjs();
  const diffMs = tripTime.diff(now);

  // if (status === "scheduled") {
  //   if (diffMs <= 0) return "";
  //   const dur = dayjs.duration(diffMs);
  //   const hr = Math.floor(dur.asHours());
  //   const min = dur.minutes();
  //   return hr === 1
  //     ? t("orderStatus.inTime", { hr, min })
  //     : t("orderStatus.inTimePlural", { hr, min });
  // }
  if (status === "scheduled") {
    if (diffMs <= 0) return "";
    const dur = dayjs.duration(diffMs);
    const hr = Math.floor(dur.asHours());
    const min = dur.minutes();
    return t("tripScheduledCountdown", { hr, min });
  }
  return "";
};